import { type CSSProperties, useCallback, useEffect, useRef, useState } from "react"

import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

import type { SourceSnippet } from "../types"

import {
  Book,
  ChevronDown,
  Eye,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { knownPage } from "../utils/source-page"

/** The page line for a snippet, or null when its page is unknown (0 or null). */
function usePageLabel(page: number | null | undefined): string | null {
  const { t } = useTranslation()
  const known = knownPage(page)
  return known === undefined ? null : t("tender.snippet.page", { page: known })
}

type SnippetCardProps = {
  snippet: SourceSnippet
  className?: string
  onOpenPreview?: () => void
}

export function FileTypeIcon({ filename, className }: { filename: string; className?: string }) {
  const ext = filename.split(".").pop()?.toLowerCase() ?? ""
  if (ext === "pdf")
    return <FileText aria-hidden className={cn("text-destructive size-4 shrink-0", className)} />
  if (["xls", "xlsx", "csv"].includes(ext))
    return <FileSpreadsheet aria-hidden className={cn("text-success size-4 shrink-0", className)} />
  if (["doc", "docx"].includes(ext))
    return (
      <FileText aria-hidden className={cn("text-accent-foreground size-4 shrink-0", className)} />
    )
  return <FileIcon aria-hidden className={cn("text-muted-foreground size-4 shrink-0", className)} />
}

function SourceIcon({ snippet, className }: { snippet: SourceSnippet; className?: string }) {
  if (snippet.kind === "library") {
    return <Book aria-hidden className={cn("text-muted-foreground size-4 shrink-0", className)} />
  }
  return <FileTypeIcon className={className} filename={snippet.documentName} />
}

export function SnippetCard({ snippet, className, onOpenPreview }: SnippetCardProps) {
  const { t } = useTranslation()
  const pageLabel = usePageLabel(snippet.page)
  const [showQuote, setShowQuote] = useState(true)
  const hasQuote = Boolean(snippet.quote)
  const isLibrary = snippet.kind === "library"

  return (
    <div className={cn("", className)}>
      <div className="group flex items-center gap-2 py-1.5">
        <Button
          aria-label={showQuote ? t("tender.snippet.hideQuote") : t("tender.snippet.showQuote")}
          className="group/icon relative -m-1 size-6 shrink-0 p-0"
          size="icon"
          type="button"
          variant="ghost"
          onClick={() => {
            if (hasQuote) setShowQuote((v) => !v)
          }}
        >
          <span className="absolute inset-0 flex items-center justify-center transition-opacity group-hover/icon:opacity-0">
            <SourceIcon snippet={snippet} />
          </span>
          <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover/icon:opacity-100">
            <ChevronDown
              className={cn(
                "text-muted-foreground size-4 transition-transform",
                !showQuote && "-rotate-90"
              )}
            />
          </span>
        </Button>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm leading-tight font-medium">{snippet.documentName}</p>
          {isLibrary ? (
            <p className="text-muted-foreground truncate text-xs">
              {snippet.libraryName}
              {pageLabel && <> · {pageLabel}</>}
            </p>
          ) : (
            pageLabel && <p className="text-muted-foreground text-xs">{pageLabel}</p>
          )}
        </div>

        {onOpenPreview && (
          <Button
            aria-label={t("tender.snippet.viewDocument")}
            className="text-muted-foreground hover:text-foreground size-7 shrink-0 p-0 opacity-0 transition-[color,background-color,opacity] group-hover:opacity-100"
            size="icon"
            type="button"
            variant="ghost"
            onClick={onOpenPreview}
          >
            <Eye className="size-3.5" />
          </Button>
        )}
      </div>

      {hasQuote && showQuote && (
        <div className="border-border/40 ml-2 border-l-2 py-1 pl-3">
          <p className="text-muted-foreground line-clamp-4 text-xs leading-relaxed italic">
            "{snippet.quote}"
          </p>
        </div>
      )}
    </div>
  )
}

// --- SnippetPopover: portal-based peek card (same approach as CitationInlinePeek) ---

const CARD_COLLAPSED_WIDTH_PX = 360
const EDGE_MARGIN_PX = 12
const CARD_OFFSET_PX = 8
const CARD_COLLAPSED_MAX_HEIGHT_PX = 380

function getPreferredPlacement(anchorRect: DOMRect): "below" | "above" {
  const availableBelow = window.innerHeight - anchorRect.bottom - CARD_OFFSET_PX - EDGE_MARGIN_PX
  const availableAbove = anchorRect.top - CARD_OFFSET_PX - EDGE_MARGIN_PX
  return availableBelow >= availableAbove ? "below" : "above"
}

function getCardLayout(
  anchorRect: DOMRect,
  widthPx: number,
  layoutOptions: { targetMaxHeightPx: number; placement: "below" | "above" }
): { left: number; maxHeight: number; top?: number; bottom?: number } {
  const { targetMaxHeightPx, placement } = layoutOptions
  const maxLeft = Math.max(EDGE_MARGIN_PX, window.innerWidth - widthPx - EDGE_MARGIN_PX)
  const left = Math.min(Math.max(anchorRect.left, EDGE_MARGIN_PX), maxLeft)

  const availableBelow = Math.max(
    0,
    window.innerHeight - anchorRect.bottom - CARD_OFFSET_PX - EDGE_MARGIN_PX
  )
  const availableAbove = Math.max(0, anchorRect.top - CARD_OFFSET_PX - EDGE_MARGIN_PX)
  const availableOnSide = placement === "below" ? availableBelow : availableAbove
  const maxHeight = Math.max(0, Math.min(targetMaxHeightPx, availableOnSide))

  if (placement === "below") {
    const naturalTop = anchorRect.bottom + CARD_OFFSET_PX
    const maxBottomTop = window.innerHeight - maxHeight - EDGE_MARGIN_PX
    const top = Math.max(EDGE_MARGIN_PX, Math.min(naturalTop, maxBottomTop))
    return { left, maxHeight, top }
  }

  const naturalBottom = window.innerHeight - anchorRect.top + CARD_OFFSET_PX
  const bottom = Math.max(EDGE_MARGIN_PX, naturalBottom)
  return { left, maxHeight, bottom }
}

type SnippetPopoverProps = {
  snippet: SourceSnippet
  documentUrl?: string
  reasoning?: string
  onOpenPreview?: () => void
}

function SnippetTriggerContent({ snippet }: { snippet: SourceSnippet }) {
  const pageLabel = usePageLabel(snippet.page)
  const isLibrary = snippet.kind === "library"
  return (
    <>
      <SourceIcon snippet={snippet} />
      <div className="min-w-0">
        <p className="text-muted-foreground group-hover:text-foreground truncate text-xs transition-colors">
          {snippet.documentName}
        </p>
        {isLibrary ? (
          <p className="text-muted-foreground/70 truncate text-xs">
            {snippet.libraryName}
            {pageLabel && <> · {pageLabel}</>}
          </p>
        ) : (
          pageLabel && <p className="text-muted-foreground/70 text-xs">{pageLabel}</p>
        )}
      </div>
    </>
  )
}

export function SnippetPopover({ snippet, reasoning, onOpenPreview }: SnippetPopoverProps) {
  const { t } = useTranslation()
  const pageLabel = usePageLabel(snippet.page)
  const [isOpen, setIsOpen] = useState(false)
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  const open = useCallback(() => {
    if (!triggerRef.current) return
    setAnchorRect(triggerRef.current.getBoundingClientRect())
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Close on outside click / Escape
  useEffect(() => {
    if (!isOpen) return

    const handlePointerDown = (e: PointerEvent) => {
      if (
        cardRef.current?.contains(e.target as Node) ||
        triggerRef.current?.contains(e.target as Node)
      ) {
        return
      }
      close()
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }

    document.addEventListener("pointerdown", handlePointerDown)
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen, close])

  // Update anchor rect on scroll/resize
  useEffect(() => {
    if (!isOpen) return

    const update = () => {
      if (triggerRef.current) {
        setAnchorRect(triggerRef.current.getBoundingClientRect())
      }
    }

    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [isOpen])

  const handleOpenPreview = useCallback(() => {
    if (onOpenPreview) {
      onOpenPreview()
      close()
    }
  }, [onOpenPreview, close])

  if (!isOpen || !anchorRect) {
    return (
      <Button
        ref={triggerRef}
        className="group h-auto w-full min-w-0 justify-start gap-1.5 overflow-hidden p-0 text-left font-normal"
        type="button"
        variant="ghost"
        onClick={open}
      >
        <SnippetTriggerContent snippet={snippet} />
      </Button>
    )
  }

  const cardWidth = Math.min(CARD_COLLAPSED_WIDTH_PX, window.innerWidth - EDGE_MARGIN_PX * 2)
  const placement = getPreferredPlacement(anchorRect)
  const layout = getCardLayout(anchorRect, cardWidth, {
    targetMaxHeightPx: CARD_COLLAPSED_MAX_HEIGHT_PX,
    placement,
  })
  const cardStyle: CSSProperties = {
    bottom: layout.bottom !== undefined ? `${layout.bottom}px` : undefined,
    left: `${layout.left}px`,
    maxHeight: `${layout.maxHeight}px`,
    overflowY: "auto",
    position: "fixed",
    top: layout.top !== undefined ? `${layout.top}px` : undefined,
    width: `${cardWidth}px`,
  }

  const hasQuote = Boolean(snippet.quote)

  return (
    <>
      <Button
        ref={triggerRef}
        className="group h-auto w-full min-w-0 justify-start gap-1.5 overflow-hidden p-0 text-left font-normal"
        type="button"
        variant="ghost"
        onClick={close}
      >
        <SnippetTriggerContent snippet={snippet} />
      </Button>
      {createPortal(
        <div
          ref={cardRef}
          className="bg-background/80 text-popover-foreground z-[60] space-y-3 rounded-md border p-4 shadow-xl backdrop-blur-md"
          style={cardStyle}
        >
          {reasoning && (
            <div className="space-y-1.5">
              <p className="text-muted-foreground text-xs font-medium">
                {t("tender.bidReview.popover.reasoning")}
              </p>
              <p className="text-sm leading-relaxed">{reasoning}</p>
            </div>
          )}

          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs font-medium">
              {t("tender.snippet.source")}
            </p>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <SourceIcon snippet={snippet} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm leading-tight font-medium">
                    {snippet.documentName}
                  </p>
                  {snippet.kind === "library" ? (
                    <p className="text-muted-foreground truncate text-xs">
                      {snippet.libraryName}
                      {pageLabel && <> · {pageLabel}</>}
                    </p>
                  ) : (
                    pageLabel && <p className="text-muted-foreground text-xs">{pageLabel}</p>
                  )}
                </div>
                {onOpenPreview && (
                  <Button
                    aria-label={t("tender.snippet.openDocument")}
                    className="text-muted-foreground hover:text-foreground size-7 shrink-0 p-0"
                    size="icon"
                    type="button"
                    variant="ghost"
                    onClick={handleOpenPreview}
                  >
                    <Eye className="size-3.5" />
                  </Button>
                )}
              </div>
              {hasQuote && (
                <div className="border-border/40 ml-2 border-l-2 py-1 pl-3">
                  <p className="text-muted-foreground line-clamp-5 text-xs leading-relaxed italic">
                    "{snippet.quote}"
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
